import {
    DATA_SOURCES,
    GOOGLE_COUNTRIES,
    INSTAGRAM_COUNTRIES,
    TIKTOK_COUNTRIES,
} from '@/config/constants';

/**
 * One descriptor per data source.
 *
 * The same per-source facts were previously spelled out in six places, in three
 * different naming spaces — `API_DATA_SOURCE` in `utils/entities`, a second
 * `DATA_SOURCES` in `utils/metrics`, `SOURCE_VERSION_KEY` in `useEndDate`, the
 * literal query specs in `useDataSourceVersion`, and cluster and query-key
 * prefixes inlined in each data hook. Adding a source meant finding all six.
 *
 * Deliberately a leaf: it imports only from `@/config/constants`. Anything that
 * needs an api function takes it as an argument rather than importing it here,
 * because `utils/entities` and `utils/metrics` both import this and a cycle
 * through an api module would be very hard to see.
 */
export const SOURCES = {
    [DATA_SOURCES.GOOGLE]: {
        key: DATA_SOURCES.GOOGLE,
        label: 'Google',
        apiName: 'google',
        routeSlug: 'google',
        versionQueryKey: 'google-version',
        versionResultKey: 'googleVersion',
        supportedGeos: GOOGLE_COUNTRIES,
        analyticsMode: 'Search',
        parentNameFilterKey: 'parentName',
        clusterPrefix: null,
    },
    [DATA_SOURCES.TIKTOK]: {
        key: DATA_SOURCES.TIKTOK,
        label: 'TikTok',
        apiName: 'tiktok',
        routeSlug: 'tiktok',
        versionQueryKey: 'tiktok-version',
        versionResultKey: 'tiktokVersion',
        supportedGeos: TIKTOK_COUNTRIES,
        analyticsMode: 'TikTok',
        parentNameFilterKey: 'tikTokParentName',
        dataQueryKeyPrefix: 'TikTokData',
        clusterPrefix: 'tiktok ',
        volumeKey: 'non_cum_avg_weekly_views',
        term: { plural: 'hashtags', singular: 'hashtag' },
    },
    [DATA_SOURCES.INSTAGRAM]: {
        key: DATA_SOURCES.INSTAGRAM,
        label: 'Instagram',
        apiName: 'instagram',
        routeSlug: 'instagram',
        versionQueryKey: 'instagram-version',
        versionResultKey: 'instagramVersion',
        supportedGeos: INSTAGRAM_COUNTRIES,
        analyticsMode: 'Instagram',
        parentNameFilterKey: 'instagramParentName',
        dataQueryKeyPrefix: 'InstagramData',
        clusterPrefix: 'instagram ',
        volumeKey: 'non_cum_avg_weekly_likes',
        term: { plural: 'hashtags', singular: 'hashtag' },
    },
    [DATA_SOURCES.POPULARITY]: {
        key: DATA_SOURCES.POPULARITY,
        label: 'Popularity',
        apiName: 'pop_index',
        // `useEndDate` is called with both spellings today; both must resolve.
        routeSlug: 'pop-index',
        routeSlugAliases: ['popularity'],
        versionQueryKey: 'popularity-version',
        versionResultKey: 'popularityVersion',
        supportedGeos: TIKTOK_COUNTRIES,
        analyticsMode: 'Overview',
        parentNameFilterKey: 'popIndexParentName',
        clusterPrefix: null,
    },
};

export const sourceOf = (key) => SOURCES[String(key).toUpperCase()];

/** By the `useEndDate` / route spelling rather than the SCREAMING_CASE key. */
export const sourceByRouteSlug = (slug) =>
    Object.values(SOURCES).find(
        (source) =>
            source.routeSlug === slug ||
            source.routeSlugAliases?.includes(slug),
    );

/**
 * The URL query-parameter name each source uses to filter a related-entity
 * table by its parent. These are live query parameters in customer bookmarks
 * and shared links, so they are parameterised here, never renamed.
 */
export const PARENT_NAME_FILTER_KEYS = Object.values(SOURCES)
    .map(({ parentNameFilterKey }) => parentNameFilterKey)
    .filter(Boolean);

/**
 * Resolves a route slug to its parent-name key, falling back to pop-index —
 * whose routes carry no source slug at all. `||` rather than `??`, matching the
 * maps this replaces and the descriptor's use of a falsy value for "none".
 */
export const parentNameFilterKeyForRouteSlug = (slug) =>
    sourceByRouteSlug(slug)?.parentNameFilterKey ||
    SOURCES[DATA_SOURCES.POPULARITY].parentNameFilterKey;
